import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import StoreX, { store } from '../../redux/oldStore';
import { GetPeoplePerson, PasswordRequirements } from '../../utils/CommonTypes/PeoplePageTypes';
import { ServerResponse } from '../../utils/Server';
import Icon, { IconType } from '../Icon/Icon';
import redux, { IState, ReduxMap } from './../../redux/redux';

interface ChangePasswordFormProps {
  person?: GetPeoplePerson;
  setModalOpen?: (open: boolean) => void;
  isSelfPasswordChange: boolean;
}

const ChangePasswordForm = (props: ChangePasswordFormProps) => {
  const [mePerson, setMePerson] = useState<GetPeoplePerson>();
  const [oldPasswordVal, setOldPasswordVal] = useState('');
  const [passwordVal, setPasswordVal] = useState('');
  const [passwordReqs, setPasswordReqs] = useState<PasswordRequirements>();
  const [confirmPasswordVal, setConfirmPasswordVal] = useState('');
  const [passwordError, setPasswordError] = useState(false);
  const [isPasswordRecovery, setIsPasswordRecovery] = useState(false);
  const [passwordChanged, setPasswordChanged] = useState(false);
  const [token, setToken] = useState<string>();
  const [personId, setPersonId] = useState<string>();

  useEffect(() => {
    let token = StoreX.GetQueryStringValue('token');
    let personId = StoreX.GetQueryStringValue('p');

    if (token && personId) {
      setIsPasswordRecovery(true);
      setToken(token);
      setPersonId(personId);
    }

    store.server.getApi('../Person/PasswordRequirements').then((res: any) => {
      setPasswordReqs(res.Value);
    });
  }, []);

  const isAdminSetPassword = !props.isSelfPasswordChange && !isPasswordRecovery;

  const fairId = StoreX.GetQueryStringValue('f');
  const continueHref = fairId ? `/app?f=${encodeURIComponent(fairId)}` : '/app';

  const clearPasswordFields = () => {
    setOldPasswordVal('');
    setPasswordVal('');
    setConfirmPasswordVal('');
  };

  const handleChangePasswordClick = () => {
    let errors: string[] = [];
    if (props.isSelfPasswordChange && oldPasswordVal.length === 0 && !isPasswordRecovery) {
      errors.push(`What is your current password!`);
    } else if (passwordVal.length === 0) {
      errors.push(`Please provide a new password.`);
    } else if (!isAdminSetPassword && passwordVal !== confirmPasswordVal) {
      errors.push(`New passwords do not match.`);
    }

    if (errors.length > 0) {
      toast.error(errors[0], { autoClose: false });
      return;
    }
    //api submission
    if (!passwordError) {
      if (isPasswordRecovery) {
        store.server
          .postApi<ServerResponse<string>>('../Person/ResetPassword', {
            person:personId,
            token: token,
            password: passwordVal,
          })
          .then((res) => {
            if (!res.Success) {
              toast.error(res.Message);
              return;
            } else {
              clearPasswordFields();
              setPasswordChanged(true);
              redux.showLogin(true);
            }
          });
      } else {
        store.server
          .postApi<ServerResponse<unknown>>('../Person/ChangePassword', {
            userId: props.person?.Id,
            newPassword: passwordVal,
            oldPassword: oldPasswordVal,
          })
          .then((res) => {
            if (!res.Success) {
              toast.error(res.Message);
              return;
            } else {
              clearPasswordFields();
              if (props.isSelfPasswordChange) {
                setPasswordChanged(true);
              } else {
                toast.success('password changed');
                props.setModalOpen?.(false);
              }
            }
          });
      }
    } else {
      toast.error('password and confirm password do not match');
    }
  };
  const checkPasswordsTheSame = () => {
    if (passwordVal !== confirmPasswordVal) {
      setPasswordError(true);
    } else {
      setPasswordError(false);
    }
  };

  if (passwordChanged) {
    return (
      <div className="form-horizontal">
        <div className="alert alert-success col-sm-offset-2 col-sm-8" data-testid="password-updated">
          <h3>
            <Icon type={IconType.checkCircle} /> Password updated
          </h3>
          {isPasswordRecovery
            ? 'Your password has been updated. Sign in with your new password.'
            : 'Your password has been updated.'}
          <br />
          <br />
          {isPasswordRecovery ? (
            <button type="button" className="btn btn-secondary" onClick={() => redux.showLogin(true)}>
              <Icon type={IconType.signIn} /> Sign In
            </button>
          ) : (
            <a href={continueHref} className="btn btn-secondary" data-testid="password-updated-continue">
              Continue
            </a>
          )}
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="form-horizontal" data-testid="change-password-form">
        <h3>Change Password</h3>

        {isPasswordRecovery && (
          <>
            <h4>Reset Your Password</h4>
            Using the form below, please enter your new password.
          </>
        )}
        <div className="alert alert-info col-sm-offset-2 col-sm-8">
          <h4>Password Requirements</h4>
          <ul>
            {passwordReqs?.MinLength && <li>Password must be at least {passwordReqs?.MinLength} characters long.</li>}
            {passwordReqs?.RequireAltCase && <li>Password must contain at least one Uppercase and one Lowercase letter</li>}
            {passwordReqs?.RequireNumber && <li>Password must contain at least one number</li>}
            {passwordReqs?.RequireSpecialChar && <li>Password must contain a special character ( {passwordReqs?.specialChars} ).</li>}
          </ul>
        </div>
        <div className="">
          {props.isSelfPasswordChange && !isPasswordRecovery && (
            <div className="form-group">
              <label className="control-label col-sm-4">Current Password:</label>
              <div className={`col-sm-6 ${passwordError ? 'has-error' : ''}`}>
                <input className="form-control" value={oldPasswordVal} onChange={(e) => setOldPasswordVal(e.target.value)} type="password" />
              </div>
            </div>
          )}
          <div className="form-group">
            <label className="control-label col-sm-4">New Password:</label>
            <div className={`col-sm-6 ${passwordError ? 'has-error' : ''}`}>
              <input
                className="form-control new-pswd"
                value={passwordVal}
                onChange={(e) => setPasswordVal(e.target.value?.replace(/[\t\s]/g, ''))}
                type={isAdminSetPassword ? 'text' : 'password'}
                autoComplete={isAdminSetPassword ? 'off' : 'new-password'}
                spellCheck={false}
              />
              {isAdminSetPassword && (
                <span className="help-block">They will be required to change this password to remove this default.</span>
              )}
            </div>
          </div>
          {!isAdminSetPassword && (
            <div className="form-group">
              <label className="control-label col-sm-4">Confirm New Password:</label>
              <div className={`col-sm-6 ${passwordError ? 'has-error' : ''}`}>
                <input
                  className="form-control confirm-new-pswd"
                  value={confirmPasswordVal}
                  onChange={(e) => setConfirmPasswordVal(e.target.value?.replace(/[\t\s]/g, ''))}
                  onBlur={checkPasswordsTheSame}
                  type="password"
                />
                {passwordError && (
                  <span id="ContentPlaceHolder1_passReset_CompareValidator1" className="label label-danger" style={{ display: 'inline' }}>
                    Your passwords do not match.
                  </span>
                )}
              </div>
            </div>
          )}
          <div className="form-group">
            <div className="col-sm-offset-4 col-sm-8">
              <button className="btn btn-secondary save-change-pswd" onClick={handleChangePasswordClick}>
                {' '}
                <Icon type={IconType.lock} /> Change Password
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ChangePasswordForm;
